import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import {
  ActionIcon,
  Badge,
  Group,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconCalendarOff, IconTrash } from '@tabler/icons-react';

import { BasicButton } from '@components/Button';
import {
  ManagementLayout,
  PageTitle,
  PaginatedList,
} from '@components/shared';
import { calendarDateValue } from '@util/formatters';
import { toErrorMessage } from '@util/toErrorMessage';

function dateFromIso(value) {
  const raw = calendarDateValue(value);
  return raw ? new Date(`${raw}T00:00:00`) : null;
}

function formatDate(value) {
  if (!value) return '—';
  const date = dateFromIso(value);
  if (!date) return String(value);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function ClosureForm({ initial, onSaved, onCancel }) {
  const isEdit = Boolean(initial?.id);
  const [name, setName] = useState(initial?.name || '');
  const [startDate, setStartDate] = useState(dateFromIso(initial?.start_date));
  const [endDate, setEndDate] = useState(dateFromIso(initial?.end_date));
  const [blocksSchedule, setBlocksSchedule] = useState(
    initial?.blocks_schedule !== false,
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(initial?.name || '');
    setStartDate(dateFromIso(initial?.start_date));
    setEndDate(dateFromIso(initial?.end_date));
    setBlocksSchedule(initial?.blocks_schedule !== false);
  }, [
    initial?.id,
    initial?.name,
    initial?.start_date,
    initial?.end_date,
    initial?.blocks_schedule,
  ]);

  function onSubmit(event) {
    event.preventDefault();
    const start = calendarDateValue(startDate);
    const end = calendarDateValue(endDate);
    if (!name.trim() || !start || !end) {
      toast.error('Name and dates are required');
      return;
    }
    const payload = {
      name: name.trim(),
      start_date: start,
      end_date: end,
      blocks_schedule: blocksSchedule,
    };
    setSaving(true);
    const request = isEdit
      ? axios.put(`/gym-closures/${initial.id}/`, payload)
      : axios.post('/gym-closures/', payload);
    request
      .then(({ data }) => {
        toast.success(isEdit ? 'Gym closure updated' : 'Gym closure created');
        onSaved(data.response?.[0]);
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setSaving(false));
  }

  return (
    <Paper p="md" withBorder radius="md" component="form" onSubmit={onSubmit}>
      <Title order={4} mb="sm">
        {isEdit ? 'Edit gym closure' : 'New gym closure'}
      </Title>
      <Stack gap="sm">
        <TextInput
          label="Name"
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
          required
        />
        <DatePickerInput
          label="Start"
          value={startDate}
          onChange={setStartDate}
          required
        />
        <DatePickerInput
          label="End"
          value={endDate}
          onChange={setEndDate}
          required
        />
        <Switch
          label="Blocks training schedule"
          description="When on, layout skips these dates for every athlete"
          checked={blocksSchedule}
          onChange={(event) => setBlocksSchedule(event.currentTarget.checked)}
        />
        <Group>
          <BasicButton
            text={saving ? 'Saving…' : isEdit ? 'Update closure' : 'Create closure'}
            type="submit"
            loading={saving}
            disabled={saving || !name.trim()}
            color="var(--brand-brick)"
            variant="filled"
            style={{
              background: 'var(--brand-brick)',
              borderColor: 'var(--brand-brick)',
            }}
            primary
          />
          <BasicButton
            text="Cancel"
            type="button"
            onClick={onCancel}
            disabled={saving}
            color="gray"
            variant="outline"
          />
        </Group>
      </Stack>
    </Paper>
  );
}

export default function AdminGymClosures() {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editingClosure, setEditingClosure] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [listKey, setListKey] = useState(0);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const retrieveData = useMemo(() => {
    const data = {};
    const from = calendarDateValue(fromDate);
    const to = calendarDateValue(toDate);
    if (from) data.from_date = from;
    if (to) data.to_date = to;
    return data;
  }, [fromDate, toDate]);

  function reloadClosures() {
    setListKey((key) => key + 1);
    queryClient.invalidateQueries({ queryKey: ['paginated-list'] });
    queryClient.invalidateQueries({ queryKey: ['admin-training-blocks'] });
    queryClient.invalidateQueries({ queryKey: ['admin-training-block'] });
  }

  function closeForm() {
    setFormOpen(false);
    setEditingClosure(null);
  }

  function onSaved() {
    closeForm();
    reloadClosures();
  }

  function startCreate() {
    setEditingClosure(null);
    setFormOpen(true);
  }

  function startEdit(closure) {
    setEditingClosure(closure);
    setFormOpen(true);
  }

  function onDelete(closureId) {
    if (!closureId) return;
    setDeleting(true);
    axios
      .delete(`/gym-closures/${closureId}/`)
      .then(() => {
        toast.success('Gym closure removed');
        setConfirmDeleteId(null);
        if (editingClosure?.id === closureId) closeForm();
        reloadClosures();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setDeleting(false));
  }

  return (
    <div>
      <PageTitle
        title="Gym closures"
        subtitle="Gym-wide holidays and shutdowns. Blocking closures keep training-block slots off those days."
        icon={<IconCalendarOff size={16} />}
        iconColor="red"
        actions={
          !formOpen ? (
            <BasicButton
              text="Add closure"
              onClick={startCreate}
              color="red"
              variant="light"
              leftSection={<IconCalendarOff size={16} />}
            />
          ) : null
        }
      />
      <ManagementLayout
        createComponent={
          formOpen ? (
            <ClosureForm
              initial={editingClosure}
              onSaved={onSaved}
              onCancel={closeForm}
            />
          ) : null
        }
        dataComponent={
          <div>
            <Group mb="sm" grow>
              <DatePickerInput
                label="From"
                value={fromDate}
                onChange={setFromDate}
                clearable
                placeholder="Any start"
              />
              <DatePickerInput
                label="To"
                value={toDate}
                onChange={setToDate}
                clearable
                placeholder="Any end"
              />
            </Group>
            <PaginatedList
              key={listKey}
              requestInfo={{
                url: '/retrieve-gym-closures/',
                data: retrieveData,
              }}
              displayFormatter={(item) => {
                const closure = item.original;
                const confirmingDelete = confirmDeleteId === closure.id;
                return (
                  <Paper key={closure.id} p="sm" withBorder radius="sm" mb="xs">
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Stack gap={4} style={{ minWidth: 0 }}>
                        <Text fw={700}>{closure.name}</Text>
                        <Text size="sm" c="dimmed">
                          {formatDate(closure.start_date)} – {formatDate(closure.end_date)}
                        </Text>
                      </Stack>
                      <Group gap="xs" wrap="nowrap">
                        <Badge
                          size="sm"
                          variant="light"
                          color={closure.blocks_schedule ? 'red' : 'teal'}
                        >
                          {closure.blocks_schedule ? 'Blocks' : 'Stacks'}
                        </Badge>
                        <BasicButton
                          text="Edit"
                          size="xs"
                          variant="light"
                          color="gray"
                          onClick={() => startEdit(closure)}
                          disabled={deleting}
                        />
                        {confirmingDelete ? (
                          <Group gap="xs" wrap="nowrap">
                            <BasicButton
                              text="Confirm"
                              size="xs"
                              color="red"
                              variant="filled"
                              onClick={() => onDelete(closure.id)}
                              loading={deleting}
                              disabled={deleting}
                            />
                            <BasicButton
                              text="Cancel"
                              size="xs"
                              color="gray"
                              variant="outline"
                              onClick={() => setConfirmDeleteId(null)}
                              disabled={deleting}
                            />
                          </Group>
                        ) : (
                          <ActionIcon
                            color="red"
                            variant="subtle"
                            title="Delete closure"
                            disabled={deleting}
                            onClick={() => setConfirmDeleteId(closure.id)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Group>
                  </Paper>
                );
              }}
            />
          </div>
        }
      />
    </div>
  );
}
